import React, { useMemo } from 'react';

import config from 'config';
import dynamic from 'next/dynamic';

import { Brand } from '~/shared/constants/brand';

import {
  fableticsIcons,
  yittyIcons,
  savageIcons,
  type IconName,
  isSavageIconProps,
} from './constants';

const SavageIconComponent = dynamic(() =>
  import('../../savage/SavageIcons').then((module) => module.SavageIcons)
);

const iconSetFromConfig = config.get('public.brand.iconSet');

type WhitelabelIconProps = {
  iconName: IconName;
  iconSetOverride?: (typeof Brand)[keyof typeof Brand];
  className: string;
  fullClassName?: string;
};

const WhitelabelIcon = ({
  iconName,
  iconSetOverride,
  className,
  fullClassName = `${className ? `${className} ` : ''}whitelabel-icon`,
  ...rest
}: WhitelabelIconProps) => {
  const { IconComponent, iconProps } = useMemo(() => {
    const iconSet = iconSetOverride ?? iconSetFromConfig;
    const baseIconProps = {
      ...rest,
      className: fullClassName,
    };

    if (iconSet === Brand.YITTY) {
      return {
        IconComponent: yittyIcons[iconName],
        iconProps: baseIconProps,
      };
    } else if (iconSet === Brand.SAVAGE) {
      if (isSavageIconProps(savageIcons[iconName])) {
        return {
          IconComponent: SavageIconComponent,
          iconProps: {
            ...(savageIcons[iconName] as object),
            ...baseIconProps,
          },
        };
      }

      return {
        // The type guard inference doesn't work for TS v4.x, this shouldn't be necessary in v5.
        IconComponent: savageIcons[iconName] as React.ElementType,
        iconProps: baseIconProps,
      };
    }

    return {
      IconComponent: fableticsIcons[iconName],
      iconProps: baseIconProps,
    };
  }, [iconName, iconSetOverride, fullClassName, rest]);

  return <IconComponent {...iconProps} />;
};

export default WhitelabelIcon;
